/**
 * DashboardV2 — Customizable bento/masonry dashboard.
 *
 * • KPI strip on top (always visible, full-width)
 * • Below: a CSS-columns masonry where every card packs tightly (no gaps)
 * • User can show/hide and drag-to-reorder cards via the "Customize" sheet
 * • Preferences persisted in localStorage keyed by user id
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity, AlertTriangle, Bell, CheckCircle2, ClipboardList, Clock, Award,
  TrendingUp, TrendingDown, ChevronRight, AlertCircle, Loader2,
  CheckCheck, FileText, Sparkles, BellRing, MessageSquare, Shield,
  Settings, GripVertical, Eye, EyeOff, ListChecks,
} from 'lucide-react';
import {
  LineChart, Line, AreaChart, Area, ResponsiveContainer, Tooltip,
  XAxis, YAxis, CartesianGrid, PieChart, Pie, Cell,
} from 'recharts';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { apiRequest } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from '../components/ui/sheet';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import MyChecklistWidget from '../components/widgets/MyChecklistWidget';

// ============== HELPERS ==============
const relativeTime = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};
const daysUntil = (iso) => {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / (24 * 3600 * 1000));
};
const fmtDay = (iso) => iso ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) : '';

const CARD_CLS = 'bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow';
const KPI_LABEL_CLS = 'text-[10px] font-bold tracking-[0.15em] text-slate-500 uppercase';

// ============== SHARED CARD WRAPPER ==============
function CardShell({ title, action, onActionClick, children, testid, dragHandleProps, isEditMode }) {
  return (
    <div className={CARD_CLS} data-testid={testid}>
      {(title || action) && (
        <div className="px-5 py-3 border-b border-slate-100 flex justify-between items-center">
          <div className="flex items-center gap-2">
            {isEditMode && (
              <button {...dragHandleProps} className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-700" data-testid={`drag-${testid}`}>
                <GripVertical className="w-4 h-4" />
              </button>
            )}
            {title && <h3 className="text-base font-bold tracking-tight text-slate-900">{title}</h3>}
          </div>
          {action && !isEditMode && (
            <button onClick={onActionClick} className="text-xs font-semibold text-slate-500 hover:text-red-600 flex items-center gap-1 transition-colors">
              {action} <ChevronRight className="w-3 h-3" />
            </button>
          )}
        </div>
      )}
      <div className="p-4 flex flex-col gap-2">{children}</div>
    </div>
  );
}

function CompactEmpty({ icon: Icon = Sparkles, label }) {
  return (
    <div className="py-3 px-3 flex items-center justify-center gap-2 bg-slate-50 rounded border border-dashed border-slate-200 text-sm text-slate-500">
      <Icon className="w-4 h-4 text-slate-400" /> {label}
    </div>
  );
}

function DuePill({ dueDate, completed }) {
  if (completed) return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">Done</span>;
  const days = daysUntil(dueDate);
  if (days === null) return null;
  if (days < 0) return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-100">Overdue {Math.abs(days)}d</span>;
  if (days <= 7) return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100">Due in {days}d</span>;
  return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-50 text-slate-600 border border-slate-200">{fmtDay(dueDate)}</span>;
}

// ============== WIDGETS ==============
function PerformanceWidget({ perf, perfScore, scores, isEditMode, dragHandleProps, navigate }) {
  return (
    <CardShell title="My Performance" action="Open" onActionClick={() => navigate('/my-performance')} testid="card-my-performance" isEditMode={isEditMode} dragHandleProps={dragHandleProps}>
      <div className="flex items-center gap-4">
        <div className="w-24 h-24 relative shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={[{ v: perfScore }, { v: Math.max(10 - perfScore, 0) }]} dataKey="v" innerRadius={28} outerRadius={42} startAngle={90} endAngle={-270}>
                <Cell fill="#10b981" /><Cell fill="#f1f5f9" />
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-xl font-black tracking-tighter">{perfScore ? perfScore.toFixed(1) : '—'}</span>
            <span className="text-[9px] text-slate-400 uppercase tracking-wider">of 10</span>
          </div>
        </div>
        <div className="flex-1 space-y-1.5 min-w-0">
          {scores.map(b => (
            <div key={b.k}>
              <div className="flex justify-between text-[10px] text-slate-500 mb-0.5">
                <span className="font-semibold truncate">{b.k}</span><span>{b.v ? Number(b.v).toFixed(1) : '—'}</span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full" style={{ width: `${(b.v || 0) * 10}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </CardShell>
  );
}

function TrendChartWidget({ chartData, isEditMode, dragHandleProps, navigate }) {
  return (
    <CardShell title="Performance Trend" action="View Report" onActionClick={() => navigate('/my-performance')} testid="card-trend-chart" isEditMode={isEditMode} dragHandleProps={dragHandleProps}>
      {chartData.length > 0 ? (
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gCompleted" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} domain={[0, 10]} />
              <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }} />
              <Area type="monotone" dataKey="completed" stroke="#10b981" strokeWidth={2} fill="url(#gCompleted)" name="Score" />
              <Line type="monotone" dataKey="engagement" stroke="#6366f1" strokeWidth={2} dot={false} name="Engagement" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <CompactEmpty icon={TrendingUp} label="Trend will appear once you have a week of activity" />
      )}
    </CardShell>
  );
}

function ExpiryWidget({ list, isEditMode, dragHandleProps, navigate }) {
  return (
    <CardShell title="Expiry Alerts" action="All" onActionClick={() => navigate('/compliance')} testid="card-expiry-alerts" isEditMode={isEditMode} dragHandleProps={dragHandleProps}>
      {list.length === 0 ? <CompactEmpty icon={Shield} label="No expiring documents" /> : list.map(c => {
        const days = c.days;
        const pill = days < 0 ? 'bg-red-50 text-red-700 border-red-200' :
                     days < 7 ? 'bg-red-50 text-red-700 border-red-100' :
                     days < 30 ? 'bg-amber-50 text-amber-700 border-amber-100' :
                     'bg-emerald-50 text-emerald-700 border-emerald-100';
        return (
          <button key={c.id} onClick={() => navigate(c.path)} className="text-left flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors">
            <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center shrink-0"><FileText className="w-4 h-4 text-amber-600" /></div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{c.name}</p>
              <p className="text-[11px] text-slate-400">{fmtDay(c.expires_at)}</p>
            </div>
            <span className={`text-[10px] font-bold px-2 py-1 rounded-full border ${pill} whitespace-nowrap`}>
              {days < 0 ? `Overdue ${Math.abs(days)}d` : `${days}d`}
            </span>
          </button>
        );
      })}
    </CardShell>
  );
}

function IssueWidget({ incidents, isEditMode, dragHandleProps, navigate }) {
  return (
    <CardShell title="Issue Reporting" action="View" onActionClick={() => navigate('/incidents')} testid="card-issues" isEditMode={isEditMode} dragHandleProps={dragHandleProps}>
      <div className="grid grid-cols-3 gap-2">
        {[
          { k: 'Open', v: incidents.new || 0, c: 'red', icon: AlertCircle },
          { k: 'In Progress', v: incidents.in_progress || 0, c: 'amber', icon: Clock },
          { k: 'Resolved', v: incidents.resolved || 0, c: 'emerald', icon: CheckCircle2 },
        ].map(({ k, v, c, icon: Icon }) => (
          <button key={k} onClick={() => navigate(`/incidents?status=${k.toLowerCase().replace(' ', '_')}`)}
                  className="flex flex-col items-start gap-1 p-2.5 rounded-lg border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all">
            <Icon className={`w-4 h-4 text-${c}-500`} />
            <span className="text-xl font-black text-slate-900 leading-none">{v}</span>
            <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{k}</span>
          </button>
        ))}
      </div>
    </CardShell>
  );
}

function UrgentTasksWidget({ list, isEditMode, dragHandleProps, navigate }) {
  return (
    <CardShell title="Urgent Tasks" action="See all" onActionClick={() => navigate('/tasks')} testid="card-urgent-tasks" isEditMode={isEditMode} dragHandleProps={dragHandleProps}>
      {list.length === 0 ? <CompactEmpty icon={CheckCircle2} label="All caught up" /> : list.map(task => (
        <button key={task.id} onClick={() => navigate(task.path || `/tasks/${task.id}`)}
                className="text-left flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors">
          <div className="w-1 h-10 bg-red-500 rounded-full mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900 truncate">{task.title}</p>
            <p className="text-[11px] text-slate-500 truncate">{task.project_name || task.category || 'Task'}</p>
          </div>
          <DuePill dueDate={task.due_date} completed={task.status === 'completed'} />
        </button>
      ))}
    </CardShell>
  );
}

function RemindersWidget({ list, isEditMode, dragHandleProps, navigate }) {
  return (
    <CardShell title="Reminders" action="All" onActionClick={() => navigate('/reminders')} testid="card-reminders" isEditMode={isEditMode} dragHandleProps={dragHandleProps}>
      {list.length === 0 ? <CompactEmpty icon={BellRing} label="No reminders today" /> : list.slice(0, 5).map(r => {
        const days = daysUntil(r.remind_at);
        const overdue = days !== null && days < 0 && !r.acknowledged;
        return (
          <button key={r.id} onClick={() => navigate(r.path)}
                  className={`text-left flex items-center gap-3 p-2 rounded-lg transition-colors ${overdue ? 'bg-slate-50 hover:bg-slate-100 opacity-90' : 'hover:bg-slate-50'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${overdue ? 'bg-red-50' : r.acknowledged ? 'bg-emerald-50' : 'bg-amber-50'}`}>
              <Bell className={`w-4 h-4 ${overdue ? 'text-red-500' : r.acknowledged ? 'text-emerald-600' : 'text-amber-600'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium truncate ${overdue ? 'line-through text-slate-400 decoration-slate-300' : 'text-slate-900'}`}>{r.title}</p>
              <p className="text-[11px] text-slate-400">{fmtDay(r.remind_at)}</p>
            </div>
            {overdue && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-100">Overdue</span>}
          </button>
        );
      })}
    </CardShell>
  );
}

function MiniCalendar({ events, onSelect }) {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const startWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const eventDates = new Set((events || []).map(e => (e.date || e.start_date || e.start_time || e.scheduled_at || '').slice(0, 10)));
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  return (
    <div>
      <div className="grid grid-cols-7 gap-1 text-[10px] text-slate-400 font-semibold uppercase tracking-wider text-center mb-1">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <div key={i}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => {
          if (c === null) return <div key={i} />;
          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(c).padStart(2, '0')}`;
          const isToday = c === today.getDate();
          const hasEvent = eventDates.has(dateStr);
          return (
            <button key={i} onClick={() => onSelect(dateStr)}
                    className={`relative aspect-square text-xs rounded transition-colors flex items-center justify-center ${isToday ? 'bg-red-600 text-white font-bold' : 'text-slate-700 hover:bg-slate-100'}`}>
              {c}
              {hasEvent && !isToday && <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-red-500" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CalendarWidget({ events, isEditMode, dragHandleProps, navigate }) {
  return (
    <CardShell title={new Date().toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })} action="Open" onActionClick={() => navigate('/calendar')} testid="card-calendar" isEditMode={isEditMode} dragHandleProps={dragHandleProps}>
      <MiniCalendar events={events} onSelect={(day) => navigate(`/calendar?date=${day}`)} />
    </CardShell>
  );
}

function ActivityWidget({ list, isEditMode, dragHandleProps }) {
  const navigate = useNavigate();
  return (
    <CardShell title="Recent Activity" testid="card-activity" isEditMode={isEditMode} dragHandleProps={dragHandleProps}>
      {list.length === 0 ? <CompactEmpty icon={Activity} label="No recent activity" /> : list.map(a => {
        const ICONS = { task: CheckCircle2, reminder: Bell, chat: MessageSquare, incident: AlertCircle, project: ClipboardList };
        const COLORS = { task: 'emerald', reminder: 'amber', chat: 'indigo', incident: 'red', project: 'slate' };
        const k = Object.keys(ICONS).find(key => (a.type || '').includes(key)) || 'task';
        const Icon = ICONS[k];
        return (
          <button key={a.id} onClick={() => navigate(a.path)} className="text-left flex items-start gap-2 p-2 rounded-lg hover:bg-slate-50 transition">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 bg-${COLORS[k]}-50 text-${COLORS[k]}-600`}>
              <Icon className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-slate-900 truncate">{a.title}</p>
              <p className="text-[10px] text-slate-400 truncate">{a.subtitle}</p>
              <p className="text-[10px] text-slate-400 mt-0.5">{relativeTime(a.at)}</p>
            </div>
          </button>
        );
      })}
    </CardShell>
  );
}

// ============== KPI TILE ==============
// Layout intentionally stacks icon + label vertically so the label has the
// FULL tile width to itself — preventing the truncation seen at narrow
// columns (e.g. "PERFORMA" / "ENGAGEME" being cut off when 5 tiles squeeze
// onto a small viewport). The label can wrap to a second line and the
// numeric value is allowed to scale down on very narrow tiles.
function KPITile({ label, value, suffix, delta, deltaLabel, trend, color = 'slate', icon: Icon, testid }) {
  const palette = {
    red: 'text-red-600 bg-red-50',
    amber: 'text-amber-600 bg-amber-50',
    emerald: 'text-emerald-600 bg-emerald-50',
    indigo: 'text-indigo-600 bg-indigo-50',
    slate: 'text-slate-600 bg-slate-100',
  }[color] || 'text-slate-600 bg-slate-100';
  const deltaUp = (delta ?? 0) > 0;
  return (
    <div className={`${CARD_CLS} p-3 sm:p-4 min-w-0 overflow-hidden`} data-testid={testid}>
      <div className="flex items-center gap-2 min-w-0">
        {Icon && (
          <div className={`shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-md flex items-center justify-center ${palette}`}>
            <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </div>
        )}
        <span className="text-[9px] sm:text-[10px] font-bold tracking-[0.1em] text-slate-500 uppercase leading-tight break-words flex-1 min-w-0">
          {label}
        </span>
      </div>
      <div className="flex items-end justify-between gap-2 mt-2 min-w-0">
        <div className="flex items-baseline gap-1 min-w-0">
          <span className="text-2xl sm:text-3xl font-black tracking-tighter text-slate-900 truncate">{value}</span>
          {suffix && <span className="text-xs sm:text-sm text-slate-400 font-medium shrink-0">{suffix}</span>}
        </div>
        {trend && trend.length > 1 && (
          <div className="hidden sm:block h-9 w-[80px] lg:w-[110px] shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend.map((v, i) => ({ i, v }))}>
                <Line type="monotone" dataKey="v" stroke={deltaUp ? '#10b981' : '#ef4444'} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      {(delta !== undefined && delta !== null) && (
        <div className="mt-1 text-[10px] sm:text-[11px] flex items-center gap-1 text-slate-500 truncate">
          {deltaUp ? <TrendingUp className="w-3 h-3 text-emerald-500 shrink-0" /> : <TrendingDown className="w-3 h-3 text-red-500 shrink-0" />}
          <span className={deltaUp ? 'text-emerald-600 font-semibold' : 'text-red-600 font-semibold'}>{deltaUp ? '+' : ''}{delta}%</span>
          {deltaLabel && <span className="text-slate-400 truncate">{deltaLabel}</span>}
        </div>
      )}
    </div>
  );
}

// ============== WIDGET REGISTRY ==============
const WIDGETS_META = [
  { id: 'my_checklist', label: 'My Checklist', icon: ListChecks },
  { id: 'trend', label: 'Performance Trend', icon: TrendingUp },
  { id: 'performance', label: 'My Performance', icon: Award },
  { id: 'urgent_tasks', label: 'Urgent Tasks', icon: AlertTriangle },
  { id: 'reminders', label: 'Reminders', icon: Bell },
  { id: 'expiry', label: 'Expiry Alerts', icon: Shield },
  { id: 'issues', label: 'Issue Reporting', icon: AlertCircle },
  { id: 'calendar', label: 'Calendar', icon: ClipboardList },
  { id: 'activity', label: 'Recent Activity', icon: Activity },
];
const DEFAULT_ORDER = WIDGETS_META.map(w => w.id);
const DEFAULT_HIDDEN = [];

// ============== SORTABLE WIDGET WRAPPER ==============
function SortableWidget({ id, isEditMode, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: !isEditMode });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    breakInside: 'avoid',
    marginBottom: '16px',
  };
  // Pass drag handle props to children via React.cloneElement
  const childWithProps = React.cloneElement(children, {
    isEditMode,
    dragHandleProps: { ...attributes, ...listeners },
  });
  return <div ref={setNodeRef} style={style} data-testid={`sortable-${id}`}>{childWithProps}</div>;
}

// ============== MAIN ==============
export default function DashboardV2() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.id || 'anon';
  const PREFS_KEY = `dashboard_v2_prefs_${userId}`;

  const [data, setData] = useState(null);
  const [editMode, setEditMode] = useState(false);

  // Persisted widget prefs — load lazily from localStorage so no setState in effect
  const initialPrefs = useMemo(() => {
    try {
      const raw = localStorage.getItem(PREFS_KEY);
      if (raw) {
        const p = JSON.parse(raw);
        const orderPref = Array.isArray(p.order)
          ? (() => {
              const merged = [...p.order.filter(id => DEFAULT_ORDER.includes(id))];
              DEFAULT_ORDER.forEach(id => { if (!merged.includes(id)) merged.push(id); });
              return merged;
            })()
          : DEFAULT_ORDER;
        const hiddenPref = Array.isArray(p.hidden) ? p.hidden : DEFAULT_HIDDEN;
        return { order: orderPref, hidden: hiddenPref };
      }
    } catch { /* ignore */ }
    return { order: DEFAULT_ORDER, hidden: DEFAULT_HIDDEN };
  }, [PREFS_KEY]);

  const [order, setOrder] = useState(initialPrefs.order);
  const [hidden, setHidden] = useState(initialPrefs.hidden);

  // Persist prefs
  const savePrefs = useCallback((nextOrder, nextHidden) => {
    try {
      localStorage.setItem(PREFS_KEY, JSON.stringify({
        order: nextOrder ?? order,
        hidden: nextHidden ?? hidden,
      }));
    } catch { /* ignore */ }
  }, [PREFS_KEY, order, hidden]);

  const loadAll = useCallback(async () => {
    const safe = (p) => p.then(d => d).catch(() => null);
    const [tasks, taskStats, reminders, perf, expiry, myIncidents, cal, notifs, hiPriority] = await Promise.all([
        safe(apiRequest('/task-management/tasks?assigned_to_me=true&limit=10')),
        safe(apiRequest('/task-management/tasks/stats')),
        safe(apiRequest('/reminders/today')),
        safe(apiRequest('/performance/my')),
        safe(apiRequest('/notifications/expiring-certificates?days_ahead=90')),
        safe(apiRequest('/incident-reporting/my-incidents')),
        safe(apiRequest(`/my-calendar/events?start_date=${monthStartISO()}&end_date=${monthEndISO()}`)),
        safe(apiRequest('/notifications?limit=10')),
        safe(apiRequest('/projects/my-high-priority-tasks')),
      ]);

      const mine = taskStats?.assigned_to_me || {};
      const taskBucket = {
        completed: mine.completed ?? 0,
        pending: mine.pending ?? 0,
        overdue: mine.overdue ?? 0,
      };
      const chartData = buildChartData(taskStats, perf);

      const expiryList = (expiry?.expiring_certificates || [])
        .map(c => ({
          id: c.id, name: c.name || c.cert_name || 'Certificate',
          path: '/employee-certificates',
          expires_at: c.expiry_date, days: daysUntil(c.expiry_date),
        }))
        .filter(c => c.days !== null)
        .sort((a, b) => (a.days || 0) - (b.days || 0))
        .slice(0, 5);

      const todayList = reminders?.today || [];
      const overdueList = reminders?.overdue || [];
      const remList = [...overdueList, ...todayList].map(r => ({
        id: r.id, title: r.title || r.message,
        remind_at: r.remind_at, acknowledged: r.is_acknowledged || false,
        path: '/reminders',
      })).sort((a, b) => (a.remind_at || '').localeCompare(b.remind_at || ''));

      const myTasks = (hiPriority?.tasks?.length
        ? hiPriority.tasks
        : (tasks?.tasks || []).filter(t => t.status !== 'completed')
      ).slice(0, 5).map(t => ({
        id: t.id, title: t.title,
        project_name: t.project_name || t.category,
        due_date: t.due_date, status: t.status,
        path: `/projects/${t.project_id || ''}`,
      }));

      const incList = myIncidents?.incidents || [];
      const incStats = {
        new: incList.filter(i => i.status === 'new').length,
        in_progress: incList.filter(i => i.status === 'in_progress').length,
        resolved: incList.filter(i => i.status === 'resolved').length,
      };

      const activityList = (notifs?.notifications || []).map(n => ({
        id: n.id, type: n.type || 'info',
        title: n.title || 'Activity',
        subtitle: n.message || '',
        at: n.created_at, path: n.link || '/',
      })).slice(0, 6);

    return {
      tasks: { ...taskBucket, list: myTasks },
      reminders: remList,
      performance: perf,
      expiry: expiryList,
      incidents: incStats,
      calendar: (cal?.events || cal || []),
      activity: activityList,
      chartData,
    };
  }, []);

  // Load data once on mount. We use a microtask boundary to keep setState out
  // of the effect's synchronous frame (helps both the lint rule and React 19's
  // strict-mode double-mount behavior).
  useEffect(() => {
    let cancelled = false;
    queueMicrotask(async () => {
      const result = await loadAll();
      if (!cancelled) setData(result);
    });
    return () => { cancelled = true; };
  }, [loadAll]);

  // Sensors for dnd-kit
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = order.indexOf(active.id);
    const newIdx = order.indexOf(over.id);
    const next = arrayMove(order, oldIdx, newIdx);
    setOrder(next);
    savePrefs(next, null);
  };

  const toggleHidden = (id) => {
    const next = hidden.includes(id) ? hidden.filter(h => h !== id) : [...hidden, id];
    setHidden(next);
    savePrefs(null, next);
  };

  const resetPrefs = () => {
    setOrder(DEFAULT_ORDER);
    setHidden(DEFAULT_HIDDEN);
    savePrefs(DEFAULT_ORDER, DEFAULT_HIDDEN);
  };

  const visibleOrder = useMemo(() => order.filter(id => !hidden.includes(id)), [order, hidden]);

  if (!data) {
    return <div className="flex items-center justify-center h-screen"><Loader2 className="w-8 h-8 animate-spin text-red-600" /></div>;
  }

  // Build normalized perf data
  const perf = data.performance || {};
  const perfScore = perf.overall_score ?? 0;
  const kpiMap = {};
  (perf.kpi_breakdown || []).forEach(k => { kpiMap[k.id] = k.score; });
  const pick = (ids) => { for (const id of ids) if (kpiMap[id] != null) return kpiMap[id]; return null; };
  const perfBars = [
    { k: 'Quality', v: pick(['work_quality', 'quality_of_work', 'customer_interaction']) },
    { k: 'Timeliness', v: pick(['attendance_punctuality', 'timeliness']) },
    { k: 'Engagement', v: perf.engagement?.score ?? pick(['active_engagement', 'teamwork_initiative']) },
    { k: 'Compliance', v: pick(['ojt_training', 'compliance', 'compliance_adherence', 'presentation_hygiene']) },
  ];
  const trendArr = (perf.trend?.weeks || []).map(p => p.score || 0);
  const engagementScore = perf.engagement?.score ?? pick(['active_engagement']);

  // Map widget id -> rendered node
  const widgetMap = {
    my_checklist: <MyChecklistWidget />,
    trend: <TrendChartWidget chartData={data.chartData} navigate={navigate} />,
    performance: <PerformanceWidget perf={perf} perfScore={perfScore} scores={perfBars} navigate={navigate} />,
    urgent_tasks: <UrgentTasksWidget list={data.tasks?.list || []} navigate={navigate} />,
    reminders: <RemindersWidget list={data.reminders} navigate={navigate} />,
    expiry: <ExpiryWidget list={data.expiry} navigate={navigate} />,
    issues: <IssueWidget incidents={data.incidents} navigate={navigate} />,
    calendar: <CalendarWidget events={data.calendar} navigate={navigate} />,
    activity: <ActivityWidget list={data.activity} />,
  };

  const t = data.tasks || {};
  return (
    <div className="p-4 lg:p-6 space-y-5 bg-slate-50 min-h-screen" data-testid="dashboard-v2">
      {/* Greeting + Customize */}
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Hello, {user?.name?.split(' ')[0] || 'there'} 👋</h1>
          <p className="text-sm text-slate-500 mt-0.5">{new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
        </div>
        <CustomizeSheet
          editMode={editMode}
          setEditMode={setEditMode}
          order={order}
          hidden={hidden}
          toggleHidden={toggleHidden}
          resetPrefs={resetPrefs}
        />
      </div>

      {/* KPI Strip (always visible) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="kpi-strip">
        <KPITile label="Performance" value={perfScore ? perfScore.toFixed(1) : '—'} suffix={perfScore ? '/10' : ''} color="emerald" icon={Award} trend={trendArr.length ? trendArr : [perfScore || 0]} testid="kpi-performance" />
        <KPITile label="Engagement" value={engagementScore ? engagementScore.toFixed(1) : 'N/A'} color="indigo" icon={Activity} trend={trendArr} testid="kpi-engagement" />
        <KPITile label="Tasks Completed" value={t.completed ?? 0} color="emerald" icon={CheckCheck} testid="kpi-tasks-completed" />
        <KPITile label="Tasks Pending" value={t.pending ?? 0} color="amber" icon={ClipboardList} testid="kpi-tasks-pending" />
        <KPITile label="Overdue" value={t.overdue ?? 0} color="red" icon={AlertTriangle} testid="kpi-tasks-overdue" />
      </div>

      {/* Masonry container — CSS columns pack cards tightly */}
      {editMode && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-sm text-amber-800 flex items-center gap-2">
          <Settings className="w-4 h-4" />
          <span>Edit mode: drag cards by the grip handle to reorder. Open <strong>Customize</strong> again to finish.</span>
        </div>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={visibleOrder} strategy={verticalListSortingStrategy}>
          <div className="masonry-dashboard" data-testid="masonry-dashboard">
            {visibleOrder.map(id => (
              <SortableWidget key={id} id={id} isEditMode={editMode}>
                {widgetMap[id]}
              </SortableWidget>
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Inline styles for CSS-columns masonry */}
      <style>{`
        .masonry-dashboard {
          column-count: 1;
          column-gap: 16px;
        }
        @media (min-width: 768px) { .masonry-dashboard { column-count: 2; } }
        @media (min-width: 1280px) { .masonry-dashboard { column-count: 3; } }
        .masonry-dashboard > div { break-inside: avoid; }
      `}</style>
    </div>
  );
}

// ============== CUSTOMIZE SHEET ==============
function CustomizeSheet({ editMode, setEditMode, order, hidden, toggleHidden, resetPrefs }) {
  const [open, setOpen] = useState(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2" data-testid="customize-dashboard-btn">
          <Settings className="w-4 h-4" /> Customize
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Customize Dashboard</SheetTitle>
          <SheetDescription>Show, hide, or reorder the widgets on your dashboard.</SheetDescription>
        </SheetHeader>
        <div className="mt-6 space-y-2">
          {order.map(id => {
            const meta = WIDGETS_META.find(w => w.id === id);
            if (!meta) return null;
            const Icon = meta.icon;
            const isHidden = hidden.includes(id);
            return (
              <div key={id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-white" data-testid={`pref-row-${id}`}>
                <Icon className={`w-4 h-4 ${isHidden ? 'text-slate-400' : 'text-slate-700'}`} />
                <span className={`text-sm flex-1 ${isHidden ? 'text-slate-400 line-through' : 'text-slate-900 font-medium'}`}>
                  {meta.label}
                </span>
                <Switch checked={!isHidden} onCheckedChange={() => toggleHidden(id)} data-testid={`toggle-${id}`} />
              </div>
            );
          })}
        </div>
        <div className="mt-6 flex gap-2 border-t pt-4">
          <Button variant="outline" size="sm" onClick={() => { setEditMode(!editMode); setOpen(false); }} className="flex-1" data-testid="toggle-edit-mode-btn">
            {editMode ? <><Eye className="w-4 h-4 mr-1" /> Done reordering</> : <><GripVertical className="w-4 h-4 mr-1" /> Reorder widgets</>}
          </Button>
          <Button variant="ghost" size="sm" onClick={resetPrefs} className="text-slate-500" data-testid="reset-prefs-btn">
            Reset
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ============== UTILS ==============
function monthStartISO() {
  const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d.toISOString().slice(0, 10);
}
function monthEndISO() {
  const d = new Date(); d.setMonth(d.getMonth() + 1, 0); return d.toISOString().slice(0, 10);
}
function buildChartData(stats, perf) {
  if (stats && Array.isArray(stats.daily_trend) && stats.daily_trend.length > 0) {
    return stats.daily_trend.map(d => ({
      day: new Date(d.date).toLocaleDateString('en-AU', { weekday: 'short' }),
      completed: d.completed || 0,
      engagement: d.engagement || 0,
    }));
  }
  const weeks = perf?.trend?.weeks;
  if (Array.isArray(weeks) && weeks.length > 0) {
    return weeks.map((w, i) => ({
      day: w.week || `W${i + 1}`,
      completed: w.score || 0,
    }));
  }
  return [];
}
